import { z } from 'zod';
import { IMS_ROLES, type ImsRole } from './ims-roles';

/**
 * The full account-role vocabulary (Drone D1 #316) = the COP roles (IMS) PLUS the
 * external-operator PORTAL roles. This is the layer that knows about BOTH classes;
 * `IMS_ROLES` stays a clean COP-only subset (do NOT pollute it with portal roles).
 *
 * Why a separate class of role at all: an external drone-marketplace operator
 * (agency kind `drone-operator`) authenticates against the SAME auth substrate as a
 * COP operator, but must have NO access to the Common Operating Picture — only to
 * the operator-portal surface. Modelling the portal role here (rather than as just
 * "another IMS role") keeps the COP-access boundary explicit and default-deny: the
 * COP is reachable ONLY by the IMS roles, and any role outside that set — today's
 * `drone-operator`, or any future non-COP class — is walled out automatically.
 *
 * Same FK-lookup anti-clobber discipline as IMS_ROLES / agency kinds / operator-action
 * verbs: the canonical list is the const-tuple here; the DB enforces it via the
 * `valid_user_roles` FK (migration 0083 seeds the IMS roles, 0088 seeds
 * `drone-operator`), NOT a CHECK enum. A new role = one literal here + one idempotent
 * seed INSERT + schema.ts stays structural — all pinned by `user-role.test.ts`.
 */

/**
 * PORTAL (external-tenant) roles — NON-COP principal classes. Kept separate from
 * IMS_ROLES so the COP-role vocabulary is unpolluted and `roleCanAccessCop` can be a
 * clean allowlist of the IMS set.
 */
export const PORTAL_ROLES = ['drone-operator'] as const;

export const PortalRoleSchema = z.enum(PORTAL_ROLES);
export type PortalRole = z.infer<typeof PortalRoleSchema>;

/** The external drone-marketplace operator's portal role (Drone D1 #316). */
export const DRONE_OPERATOR_ROLE: PortalRole = 'drone-operator';

/**
 * The complete role vocabulary = IMS (COP) roles ∪ portal roles. This is EXACTLY the
 * set of rows in the `valid_user_roles` lookup table (migrations 0083 + 0088), and
 * the type carried on `req.operator.role`.
 */
export const USER_ROLES = [...IMS_ROLES, ...PORTAL_ROLES] as const;

export const UserRoleSchema = z.enum(USER_ROLES);
export type UserRole = ImsRole | PortalRole;

/** A pre-built set for O(1) membership checks against the COP-role subset. */
const IMS_ROLE_SET: ReadonlySet<string> = new Set<string>(IMS_ROLES);

/** Type guard: is this `UserRole` one of the COP (IMS) roles? Narrows to `ImsRole`. */
export function isImsRole(role: UserRole): role is ImsRole {
  return IMS_ROLE_SET.has(role);
}

/**
 * THE COP-access boundary predicate (Drone D1 #316) — EXPLICIT and DEFAULT-DENY.
 *
 * Returns true ONLY for the COP roles (the IMS set, INCLUDING the read-only
 * `viewer`); false for `drone-operator` and for ANY future non-COP role or unknown
 * value. It is a positive allowlist of the IMS set by design — deliberately NOT
 * derived from `roleCanPerformOperatorActions` / "not viewer", so that adding a new
 * non-COP role walls it out of the COP automatically (a new role is denied until it
 * is explicitly an IMS role). This is the brain of the server-side class gate that
 * 403s an authenticated external-operator principal on every COP route.
 *
 * Accepts an arbitrary value (typed `UserRole`, but membership-checked against the
 * IMS set) so an unexpected/stale role can never read as COP-accessible.
 */
export function roleCanAccessCop(role: UserRole): boolean {
  return IMS_ROLE_SET.has(role);
}

/**
 * Coerce an arbitrary stored / wire value into a `UserRole`, defaulting to the
 * LEAST-privileged COP role (`viewer`) on any unexpected value — mirroring
 * `parseRoleOrViewer`, but WITHOUT collapsing the portal roles: a real
 * `drone-operator` value round-trips intact (it IS a member of USER_ROLES), so the
 * COP class gate can recognise and wall it. Only a genuinely-unknown value (a DB row
 * a stale server doesn't know, never the FK-guaranteed set at rest) falls back to
 * `viewer`. The `users.role` FK already guarantees a valid value at rest; this guards
 * the read paths regardless.
 */
export function parseUserRoleOrViewer(value: unknown): UserRole {
  const parsed = UserRoleSchema.safeParse(value);
  return parsed.success ? (parsed.data as UserRole) : 'viewer';
}
